import React from 'react';
import { CalendarDays, CalendarRange, CalendarX, Clock, Lock, MessageSquare, Sun, Unlock } from 'lucide-react';

import './ListaDeBloqueos.css';

/**
 * Los horarios bloqueados que ya existen, con el botón para sacarlos.
 *
 * Es la quinta pieza de la pantalla de Turnos y la última que seguía siendo
 * markup suelto adentro de Dashboard.jsx, con sus estilos perdidos en las 9.2k
 * líneas de Dashboard.css. Por eso se desincronizó del resto: ahí sus clases
 * estaban definidas TRES veces —una versión entera que ya no se renderizaba, la
 * viva, y una tercera 400 líneas más abajo que repetía `.blocked-slot-date`,
 * `-time` y `-reason` con otro peso y otro gris—. Como la tercera iba después,
 * ganaba, y la tarjeta se veía como una mezcla de dos diseños que ninguna de
 * las dos definiciones describía.
 *
 * Las clases van prefijadas (`lista-bloqueos__…`) por lo mismo que las de
 * SelectorDeFechas: las dos hojas son globales y CRA las junta en una sola, así
 * que un nombre genérico vuelve a chocar con Dashboard.css tarde o temprano.
 *
 * Los íconos son SVG y no emojis (eran 📅 🕐 💬 🔓 🌅) para que combine con la
 * ventana que crea los bloqueos, que ya usa lucide. Van con `aria-hidden`: lo
 * que dicen está escrito al lado.
 *
 * Crear un bloqueo es otra cosa y vive en BloquearHorarios.jsx.
 */

const formatearFechaLarga = (fecha) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-AR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

const formatearFechaCorta = (fecha) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-AR');

/**
 * Qué decir de un bloqueo: el ícono y la etiqueta del tipo, la fecha y el
 * horario. Separado del render porque es la única parte con decisiones.
 */
const describirBloqueo = (bloqueo) => {
  if (bloqueo.type === 'single') {
    return {
      Icono: CalendarX,
      etiqueta: 'Día específico',
      fecha: formatearFechaLarga(bloqueo.date),
    };
  }

  if (bloqueo.type === 'range') {
    return {
      Icono: CalendarRange,
      etiqueta: 'Rango de fechas',
      fecha:
        bloqueo.rangeStartDate && bloqueo.rangeEndDate
          ? `${formatearFechaCorta(bloqueo.rangeStartDate)} al ${formatearFechaCorta(bloqueo.rangeEndDate)}`
          : formatearFechaCorta(bloqueo.date),
    };
  }

  return {
    Icono: Lock,
    etiqueta: 'Bloqueado',
    fecha: formatearFechaCorta(bloqueo.date),
  };
};

export default function ListaDeBloqueos({ bloqueos = [], onDesbloquear }) {
  if (bloqueos.length === 0) return null;

  const cantidad = bloqueos.length;

  return (
    <section className="lista-bloqueos">
      <header className="lista-bloqueos__cabecera">
        <h3>
          <Lock size={18} aria-hidden="true" />
          Horarios bloqueados
        </h3>
        <span className="lista-bloqueos__contador">
          {cantidad} {cantidad === 1 ? 'horario bloqueado' : 'horarios bloqueados'}
        </span>
      </header>

      <ul className="lista-bloqueos__items">
        {bloqueos.map((bloqueo) => {
          const { Icono, etiqueta, fecha } = describirBloqueo(bloqueo);

          return (
            <li key={bloqueo.id} className="lista-bloqueos__item">
              <div className="lista-bloqueos__datos">
                <span className="lista-bloqueos__tipo">
                  <Icono size={14} aria-hidden="true" />
                  {etiqueta}
                </span>

                <p className="lista-bloqueos__fecha">
                  <CalendarDays size={15} aria-hidden="true" />
                  {fecha}
                </p>

                <p className="lista-bloqueos__hora">
                  {bloqueo.isFullDay ? (
                    <>
                      <Sun size={14} aria-hidden="true" />
                      Todo el día
                    </>
                  ) : (
                    <>
                      <Clock size={14} aria-hidden="true" />
                      {bloqueo.startTime} a {bloqueo.endTime}
                    </>
                  )}
                </p>

                {bloqueo.reason && (
                  <p className="lista-bloqueos__motivo">
                    <MessageSquare size={14} aria-hidden="true" />
                    {bloqueo.reason}
                  </p>
                )}
              </div>

              <button
                type="button"
                className="lista-bloqueos__desbloquear"
                onClick={() => onDesbloquear(bloqueo.id)}
              >
                <Unlock size={15} aria-hidden="true" />
                Desbloquear
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
